import { motion, useAnimation, useMotionValue, useTransform } from "framer-motion";
import { Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

const SwipeableCard = ({ children, onDelete, onClick, className, style }) => {
    const x = useMotionValue(0);
    const controls = useAnimation();

    // Filter out transition and scale classes that conflict with Framer Motion drag transforms
    const cleanedClassName = className
        ? className
            .split(" ")
            .filter(c => !c.includes("transition-") && c !== "transition" && c !== "transition-all" && c !== "transition-transform" && c !== "active-scale" && c !== "home-card-entry")
            .join(" ")
        : "";

    // Transform swipe distance to background color and opacity (left swipe)
    const backgroundColor = useTransform(x, [-100, 0], ["hsl(var(--destructive))", "transparent"]);
    const iconOpacity = useTransform(x, [-100, -20], [1, 0]);
    const iconScale = useTransform(x, [-100, -20], [1, 0.5]);

    const handleDragEnd = async (_, info) => {
        // Trigger delete if swiped/flicked far enough to the left (negative x/offset/velocity)
        if (info.offset.x < -60 || x.get() < -60 || info.velocity.x < -300) {
            // Snap back because the parent will show a confirmation dialog
            controls.start({ x: 0 });
            onDelete();
        } else {
            // Snap back
            controls.start({ x: 0 });
        }
    };

    return (
        <div className={cn("relative overflow-hidden rounded-3xl group", className?.includes("home-card-entry") && "home-card-entry")} style={style}>
            {/* Delete Background Layer (Revealed on the right when swiping left) */}
            <motion.div
                style={{ backgroundColor }}
                className="absolute inset-0 flex items-center justify-end pr-6 z-0"
            >
                <motion.div 
                    style={{ opacity: iconOpacity, scale: iconScale }}
                    className="flex flex-col items-center gap-1 text-white"
                >
                    <Trash2 className="w-5 h-5 animate-pulse" />
                    <span className="text-[9px] font-bold uppercase tracking-wider">Delete</span>
                </motion.div>
            </motion.div>

            {/* Content Layer */}
            <motion.div
                drag="x"
                dragConstraints={{ left: -120, right: 0 }}
                dragElastic={0.15}
                onDragEnd={handleDragEnd}
                animate={controls}
                style={{ x }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => {
                    // Only trigger click if not dragging
                    if (Math.abs(x.get()) < 5) {
                        onClick?.(e);
                    }
                }}
                className={cn(
                    "relative z-10 cursor-pointer touch-pan-y",
                    cleanedClassName
                )}
            >
                {children}
            </motion.div>
        </div>
    );
};

export default SwipeableCard;
